import {
  cloneElement,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { DismissableLayerBranch } from "@radix-ui/react-dismissable-layer";
import * as MenuPrimitive from "@radix-ui/react-dropdown-menu";
import { OverlayScrim } from "./OverlayScrim";

export function Menu({
  trigger,
  children,
  open,
  onOpenChange,
  className = "z-float w-60 rounded-md border border-line bg-surface p-1 shadow-lg",
  align = "start",
  closeOnEscape = true,
  scrim = false,
}: {
  trigger: ReactElement<{
    onClick?: (event: MouseEvent) => void;
    onPointerDown?: (event: PointerEvent) => void;
  }>;
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
  align?: "start" | "center" | "end";
  closeOnEscape?: boolean;
  // The folder and profile menus used a full-screen layer so the outside click
  // closed them and did not land on the control underneath.
  scrim?: boolean;
}) {
  return (
    <MenuPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
      {/* A press on the trigger is not an outside press. With modal off, the
          content would close on pointerdown and the click would open it again. */}
      <DismissableLayerBranch className="contents">
        <MenuPrimitive.Trigger asChild>
          {cloneElement(trigger, {
            // Radix toggles on pointerdown. Stopping that and toggling on click
            // keeps one open per press, including in tests that click the button.
            onPointerDown: (event: PointerEvent) => {
              trigger.props.onPointerDown?.(event);
              event.preventDefault();
            },
            onClick: (event: MouseEvent) => {
              trigger.props.onClick?.(event);
              onOpenChange(!open);
            },
          })}
        </MenuPrimitive.Trigger>
      </DismissableLayerBranch>
      {scrim && open
        ? createPortal(
            <OverlayScrim onDismiss={() => onOpenChange(false)} />,
            document.body,
          )
        : null}
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          align={align}
          sideOffset={4}
          className={className}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={
            closeOnEscape ? undefined : (event) => event.preventDefault()
          }
        >
          {children}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}

export function MenuItem({
  children,
  onSelect,
  className = "flex w-full cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-left text-[12px] text-muted outline-none data-[highlighted]:bg-hover data-[highlighted]:text-fg",
}: {
  children: ReactNode;
  onSelect: () => void;
  className?: string;
}) {
  return (
    <MenuPrimitive.Item className={className} onSelect={onSelect}>
      {children}
    </MenuPrimitive.Item>
  );
}
